import React, { memo, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import TimeRangeToggle from './TimeRangeToggle';
import { useEngagementHeatmapData } from '../../hooks/useEngagementHeatmapData';
import type { EngagementTimeRange } from '../../utils/engagementAnalytics';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOURS = Array.from({ length: 24 }, (_, hour) => `${hour}:00`);

const EngagementHeatmap: React.FC = () => {
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState<EngagementTimeRange>('90d');
  const { data, isLoading, error, refetch, isFetching } = useEngagementHeatmapData(timeRange);

  const heatmap = useMemo(() => {
    return DAY_LABELS.map((label, dayIndex) => ({
      label,
      values: HOURS.map((_, hour) => data?.[dayIndex]?.[hour] ?? 0),
    }));
  }, [data]);

  const maxValue = useMemo(() => {
    return heatmap.reduce((max, row) => {
      const rowMax = Math.max(...row.values, 0);
      return Math.max(max, rowMax);
    }, 0);
  }, [heatmap]);

  const getCellColor = (value: number) => {
    if (maxValue === 0) {
      return theme.palette.action.hover;
    }
    const ratio = value / maxValue;
    const alphaValue = Math.max(0.15, Math.min(0.9, ratio));
    const baseColor = theme.palette.primary.main;
    return alpha(baseColor, alphaValue);
  };

  return (
    <Card elevation={0} sx={{ height: '100%' }}>
      <CardHeader
        title={<Typography variant="h6">Engagement Heatmap</Typography>}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <TimeRangeToggle value={timeRange} onChange={setTimeRange} disabled={isLoading} />
            <Tooltip title="Refresh heatmap">
              <span>
                <IconButton
                  onClick={() => refetch()}
                  disabled={isFetching}
                  aria-label="Refresh engagement heatmap"
                >
                  {isFetching ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        }
      />
      <CardContent>
        {error ? (
          <Alert severity="error">Unable to load heatmap data.</Alert>
        ) : isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box
              role="table"
              aria-label="Hourly engagement intensity by day of week"
              sx={{
                display: 'grid',
                gridTemplateColumns: `80px repeat(${HOURS.length}, minmax(32px, 1fr))`,
                gap: 0.5,
              }}
            >
              <Box role="row" sx={{ display: 'contents' }}>
                <Box role="columnheader" />
                {HOURS.map((hour) => (
                  <Typography
                    key={hour}
                    variant="caption"
                    color="text.secondary"
                    textAlign="center"
                    role="columnheader"
                    aria-label={`Hour ${hour}`}
                  >
                    {hour}
                  </Typography>
                ))}
              </Box>
              {heatmap.map((row) => (
                <Box key={row.label} role="row" sx={{ display: 'contents' }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'flex', alignItems: 'center' }}
                    role="rowheader"
                    aria-label={`${row.label} row`}
                  >
                    {row.label}
                  </Typography>
                  {row.values.map((value, index) => (
                    <Tooltip key={`${row.label}-${index}`} title={`${value} engagements`}>
                      <Box
                        role="gridcell"
                        aria-label={`${row.label} at ${HOURS[index]}: ${value} engagements`}
                        tabIndex={0}
                        sx={{
                          width: '100%',
                          height: 32,
                          borderRadius: 1,
                          backgroundColor: getCellColor(value),
                          opacity: maxValue === 0 ? 0.2 : Math.max(0.2, value / (maxValue || 1)),
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default memo(EngagementHeatmap);
